// src/pages/OnlineOrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { ShoppingBag } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ordersManageApi, storeApi } from '../api';
import OrderFilterTabs from '../components/orders/OrderFilterTabs';
import OrderCard from '../components/orders/OrderCard';
import AssignDeliveryDialog from '../components/orders/AssignDeliveryDialog';
import PrintLabelDocument from '../components/orders/PrintLabelDocument';

const OnlineOrdersPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const [showAssign, setShowAssign] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [assignOrderNo, setAssignOrderNo] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [assigning, setAssigning] = useState(false);

  // 打印:整个页面只保留一份打印目标数据，避免多张卡片各自渲染导致重叠
  const [printOrder, setPrintOrder] = useState(null);

  const filterTabs = isHQ
    ? [['', '全部'], ['pending', '待指派'], ['assigned', '待接单'], ['confirmed', '已接单'], ['preparing', '备货中'], ['delivering', '配送中'], ['ready', '待自提'], ['completed', '已完成']]
    : [['', '全部'], ['assigned', '待接单'], ['confirmed', '已接单'], ['preparing', '备货中'], ['delivering', '配送中'], ['ready', '待自提'], ['completed', '已完成']];

  useEffect(() => {
    loadOrders();
    if (isHQ) loadBranches();
  }, [filterStatus]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await ordersManageApi.getList(filterStatus ? { order_status: filterStatus } : {});
      if (res.success) setOrders(res.data);
    } catch (err) {
      console.error('加载订单失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await storeApi.getList();
      if (res.success) setBranches(res.data.filter(s => s.type === 'branch' && s.status === 'active'));
    } catch (err) { console.error(err); }
  };

  const toggleDetail = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const openAssign = (order) => {
    setAssignOrderId(order.id);
    setAssignOrderNo(order.order_no);
    setSelectedBranch('');
    setShowAssign(true);
  };

  const handleAssign = async () => {
    if (!selectedBranch) { alert('请选择配送方式'); return; }
    setAssigning(true);
    try {
      const isHQDelivery = selectedBranch === 'hq';
      const res = await ordersManageApi.assign(assignOrderId, {
        assigned_store_id: isHQDelivery ? currentUser.store_id : parseInt(selectedBranch),
        is_hq_delivery: isHQDelivery
      });
      if (res.success) {
        const info = res.data
          ? `\n${isHQDelivery ? '总店自行配送，全额收入' : `佣金：$${res.data.commission_amount}\n分店收入：$${res.data.branch_amount}`}`
          : '';
        alert(`指派成功！${info}`);
        setShowAssign(false);
        loadOrders();
      } else {
        alert(res.message || '指派失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '指派失败');
    } finally {
      setAssigning(false);
    }
  };

  const handleAction = async (action, order) => {
    const confirmMap = {
      confirm: '确认接单？',
      prepare: '确认开始备货？备货后将扣减库存。',
      deliver: order.delivery_type === 'pickup' ? '确认商品已备好，通知用户自提？' : '确认开始配送？',
      complete: '确认订单已完成？完成后将自动结算。'
    };
    if (!window.confirm(confirmMap[action])) return;
    try {
      let res;
      if (action === 'confirm') res = await ordersManageApi.confirm(order.id);
      else if (action === 'prepare') res = await ordersManageApi.prepare(order.id);
      else if (action === 'deliver') res = await ordersManageApi.deliver(order.id);
      else if (action === 'complete') res = await ordersManageApi.complete(order.id);
      if (res.success) { alert(res.message); loadOrders(); }
      else alert(res.message || '操作失败');
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    }
  };

  // 打印:先把要打印的订单数据设进state，等DOM更新完再触发浏览器打印
  const handlePrint = (order) => {
    setPrintOrder(order);
    setTimeout(() => window.print(), 100);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">线上订单</h2>
          <p className="text-xs text-gray-600 mt-1">
            {isHQ ? '管理所有 H5 线上订单' : '处理分配给本店的订单'}
          </p>
        </div>
        <OrderFilterTabs
          tabs={filterTabs}
          filterStatus={filterStatus}
          onChange={setFilterStatus}
          onRefresh={loadOrders}
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">加载中...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无订单</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              isExpanded={expandedId === order.id}
              isHQ={isHQ}
              isManager={isManager}
              onToggle={toggleDetail}
              onAssign={openAssign}
              onAction={handleAction}
              onPrint={handlePrint}
            />
          ))}
        </div>
      )}

      {showAssign && (
        <AssignDeliveryDialog
          orderNo={assignOrderNo}
          branches={branches}
          selectedBranch={selectedBranch}
          onSelectBranch={setSelectedBranch}
          assigning={assigning}
          onConfirm={handleAssign}
          onClose={() => setShowAssign(false)}
        />
      )}

      {/* 打印专用文档：整个页面只有这一份，平时不可见 */}
      <PrintLabelDocument order={printOrder} />
    </div>
  );
};

export default OnlineOrdersPage;